import { graphGet } from "@/helpers/api/apiGraph";
import FileHelper from "@/helpers/utilities/FileHelper";
import DriveItemHelper from "@/models/helpers/DriveItemHelper";
import TrackHelper from "@/helpers/utilities/TrackHelper";
import DriveItem from "@/models/Documents/DriveItem";

import { useInstitutionsStore } from "@/stores/institutions";
import { useSettingsStore } from "@/stores/settings";
import { useDocumentStore } from ".";

export const templates_state = {
  templateMap: {},
  templateForFolderType: {}, // { nctSystem: nctIdentifier: [templates]}
  templatesForDriveItemMap: {} // { driveId: { itemId: [templates] } }
};

export const templates_actions = {
  /**
   * Load template setup for destination drive and item
   * @param { string } destinationDriveId DriveItem of destination drive where documents will be created from templates
   * @param { string } destinationItemId DriveItem of destination drive where documents will be created from templates
   * @param { string } templateFolderDriveId DriveId of templates location
   * @param { string } templateFolderItemId ItemId of templates location
   * @returns { Promise } Promise of templates loaded
   */
  async loadTemplates(destinationDriveId, destinationItemId, templateFolderDriveId, templateFolderItemId) {
    const calculatedDestinationItemId = DriveItemHelper.getItemId(destinationItemId);

    // New template setup with nctType of destination drive item
    const destinationDriveItem = await this.loadDriveItem(destinationDriveId, calculatedDestinationItemId);

    const calculatedTemplateFolderItemId = DriveItemHelper.getItemId(templateFolderItemId);

    // Check if we already has the type in the state - and if so return it
    if (
      this.templateForFolderType[destinationDriveItem.nctSystem] &&
      this.templateForFolderType[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier] &&
      this.templateForFolderType[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier][
        calculatedTemplateFolderItemId
      ]
    ) {
      return this.templateForFolderType[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier][
        calculatedTemplateFolderItemId
      ];
    }

    if (!templateFolderItemId) {
      const nctSystem = String(destinationDriveItem.nctSystem);
      const nctIdentifier = String(destinationDriveItem.nctIdentifier);
      // ItemId is empty - we are in the root of the template folder
      let templateFolderItems = await loadTemplatesForFolderType(nctSystem, nctIdentifier);

      if (!templateFolderItems || templateFolderItems.length === 0) {
        templateFolderItems = await loadTemplatesForFolderType("other", "other");
      }

      templateFolderItems = handleTemplateItemsBeforePersist(templateFolderItems);

      const stateClone = cloneTemplateForFolderType(this.templateForFolderType) || {};
      if (!stateClone[nctSystem]) {
        stateClone[nctSystem] = {};
      }

      if (!stateClone[nctSystem][nctIdentifier]) {
        stateClone[nctSystem][nctIdentifier] = {};
      }

      stateClone[nctSystem][nctIdentifier][calculatedTemplateFolderItemId] = templateFolderItems;

      this.templateForFolderType = stateClone;
    } else {
      // ItemId har a value - we are in a subfolder in the templare folder
      if (
        this.templateForFolderType[destinationDriveItem.nctSystem] &&
        this.templateForFolderType[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier] &&
        this.templateForFolderType[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier][
          templateFolderItemId
        ]
      ) {
        return this.templateForFolderType[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier][
          templateFolderItemId
        ];
      }

      let templateItems;
      if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
        const response = await graphGet(
          `/drives/${templateFolderDriveId}/items/${templateFolderItemId}/?$expand=children`
        );
        templateItems = response.children.map(i => new DriveItem(i));
      } else {
        const response = await graphGet(
          `/drives/${templateFolderDriveId}/items/${templateFolderItemId}/children?$expand=thumbnails`
        );
        templateItems = response?.value.map(i => new DriveItem(i)) || [];
      }

      let templateFolderItems = handleTemplateItemsBeforePersist(templateItems.map(i => new DriveItem(i)));

      const stateClone = cloneTemplateForFolderType(this.templateForFolderType) || {};

      if (!stateClone[destinationDriveItem.nctSystem]) {
        stateClone[destinationDriveItem.nctSystem] = {};
      }

      if (!stateClone[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier]) {
        stateClone[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier] = {};
      }

      stateClone[destinationDriveItem.nctSystem][destinationDriveItem.nctIdentifier][templateFolderItemId] =
        templateFolderItems;

      this.templateForFolderType = stateClone;
    }
  },

  addTemplatesToState(destinationDriveId, destinationItemId, templatesFolderDriveId, templatesFolderItemId, templates) {
    const calculatedDestinationItemId = DriveItemHelper.getItemId(destinationItemId);
    const stateDriveId = templatesFolderDriveId || "root";
    const stateItemId = DriveItemHelper.getItemId(templatesFolderItemId);

    const templateMapClone = JSON.parse(JSON.stringify(this.templateMap));

    if (!templateMapClone[destinationDriveId]) {
      templateMapClone[destinationDriveId] = {};
    }

    if (!templateMapClone[destinationDriveId][calculatedDestinationItemId]) {
      templateMapClone[destinationDriveId][calculatedDestinationItemId] = {};
    }

    if (!templateMapClone[destinationDriveId][calculatedDestinationItemId][stateDriveId]) {
      templateMapClone[destinationDriveId][calculatedDestinationItemId][stateDriveId] = {};
    }

    templateMapClone[destinationDriveId][calculatedDestinationItemId][stateDriveId][stateItemId] = templates;

    this.templateMap = templateMapClone;
  }
};

export const templates_getters = {
  templatesForFolderType(state) {
    return (folderSystem, folderIdentifier, templatesFolderItemId) => {
      if (state.templateForFolderType[folderSystem] && state.templateForFolderType[folderSystem][folderIdentifier]) {
        return state.templateForFolderType[folderSystem][folderIdentifier][
          DriveItemHelper.getItemId(templatesFolderItemId)
        ];
      }
      return [];
    };
  }
};

/**
 * Load templates for destination drive item
 * @param { string } nctSystem nctSystem of destination drive where documents will be created from templates
 * @param { string } nctIdentifier nctIdentifier of destination drive where documents will be created from templates
 * @returns { Promise<Array<object>> } Promise of templates loaded
 */
async function loadTemplatesForFolderType(nctSystem, nctIdentifier) {
  const settingsStore = useSettingsStore();
  const templateStructure = settingsStore.templateStructure;
  const templateGeneralRoot = settingsStore.templateFolders?.general;
  const templateInstitutionsRoot = settingsStore.templateFolders?.institutions;

  if (!templateStructure) {
    TrackHelper.registerError("Missing template setup");
    // eslint-disable-next-line no-console
    console.log("Missing Template Setup");
    return;
  }

  // Only traverse two levels deep in the structure - add more later if needed
  let templateFolderSteps = [];

  if (nctSystem && templateStructure[nctSystem]) {
    if (Array.isArray(templateStructure[nctSystem])) {
      templateStructure[nctSystem].forEach(group => {
        if (Object.hasOwn(group, "folders")) {
          if (group.folders[nctIdentifier]) {
            const templatePathFromRoot = [];

            if (Object.hasOwn(group, "label")) {
              templatePathFromRoot.push(group.label);
            }
            templatePathFromRoot.push(group.folders[nctIdentifier].label);
            templateFolderSteps.push(templatePathFromRoot);
          }
        }
      });
    } else {
      if (Object.hasOwn(templateStructure[nctSystem], "folders")) {
        if (templateStructure[nctSystem].folders[nctIdentifier]) {
          const templatePathFromRoot = [];

          if (Object.hasOwn(templateStructure[nctSystem], "label")) {
            templatePathFromRoot.push(...templateStructure[nctSystem].label.split("/"));
          }
          templatePathFromRoot.push(templateStructure[nctSystem].folders[nctIdentifier].label);
          templateFolderSteps.push(templatePathFromRoot);
        }
      } else if (Object.hasOwn(templateStructure[nctSystem], "label")) {
        templateFolderSteps.push([templateStructure[nctSystem].label]);
      }
    }
  }

  const templateItems = [];
  if (templateFolderSteps.length) {
    // Load general templates
    if (templateGeneralRoot && templateGeneralRoot.driveId !== "" && templateGeneralRoot.itemId !== "") {
      const generalTemplatesRootResponse = await graphGet(
        `/drives/${templateGeneralRoot.driveId}/items/${templateGeneralRoot.itemId}/?$expand=children${
          import.meta.env.VITE_BUILD_TARGET === "kkfiler" ? "" : "($select=name,id)"
        }`
      );
      let generalTemplatesRoot = new DriveItem(generalTemplatesRootResponse);
      if (generalTemplatesRoot) {
        // Run through parent folders
        let actualTemplateFolder;

        for (let i = 0; i < templateFolderSteps.length; i++) {
          const templateFolderStep = templateFolderSteps[i];
          actualTemplateFolder = generalTemplatesRoot;

          for (let j = 0; j < templateFolderStep.length; j++) {
            const child = actualTemplateFolder?.children?.find(c => c.name === templateFolderStep[j]);

            if (child) {
              const actualTemplateFolderResponse = await graphGet(
                `/drives/${templateGeneralRoot.driveId}/items/${child.id}/?$expand=children${
                  import.meta.env.VITE_BUILD_TARGET === "kkfiler" ? "" : "($select=name,id)"
                }`
              );
              actualTemplateFolder = new DriveItem(actualTemplateFolderResponse);
            } else {
              actualTemplateFolder = null;
            }
          }
        }

        // Load actual template folder
        if (actualTemplateFolder) {
          let generalTemplates = [];

          if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
            const response = await graphGet(
              `/drives/${templateGeneralRoot.driveId}/items/${actualTemplateFolder.id}/?$expand=children`
            );
            generalTemplates = response.children.map(i => new DriveItem(i));
          } else {
            const response = await graphGet(
              `/drives/${templateGeneralRoot.driveId}/items/${actualTemplateFolder.id}/children?$expand=thumbnails`
            );
            if (response.value) {
              generalTemplates = response.value.map(i => new DriveItem(i));
            } else {
              generalTemplates = response.map(i => new DriveItem(i));
            }
          }

          templateItems.push(...generalTemplates);
        }
      }
    }

    // Load institution templates
    if (templateInstitutionsRoot && templateInstitutionsRoot.driveId !== "" && templateInstitutionsRoot.itemId !== "") {
      const institutionTemplatesRootResponse = await graphGet(
        `/drives/${templateInstitutionsRoot.driveId}/items/${templateInstitutionsRoot.itemId}/?$expand=children${
          import.meta.env.VITE_BUILD_TARGET === "kkfiler" ? "" : "($select=name,id)"
        }`
      );

      let institutionTemplatesRoot = new DriveItem(institutionTemplatesRootResponse);

      if (institutionTemplatesRoot) {
        const institutionStore = useInstitutionsStore();
        const activeInstitution = institutionStore.activeInstitution;

        let activeInstitutionTemplateRoot = institutionTemplatesRoot.children.find(c =>
          c.name.startsWith(activeInstitution.identifier)
        );

        if (activeInstitutionTemplateRoot) {
          const activeInstitutionTemplateRootResponse = await graphGet(
            `/drives/${templateInstitutionsRoot.driveId}/items/${activeInstitutionTemplateRoot.id}/?$expand=children${
              import.meta.env.VITE_BUILD_TARGET === "kkfiler" ? "" : "($select=name,id)"
            }`
          );

          activeInstitutionTemplateRoot = new DriveItem(activeInstitutionTemplateRootResponse);

          let actualTemplateFolder;

          for (let i = 0; i < templateFolderSteps.length; i++) {
            const templateFolderStep = templateFolderSteps[i];
            actualTemplateFolder = activeInstitutionTemplateRoot;

            for (let j = 0; j < templateFolderStep.length; j++) {
              const child = actualTemplateFolder?.children?.find(c => c.name === templateFolderStep[j]);

              if (child) {
                const actualTemplateFolderResponse = await graphGet(
                  `/drives/${templateGeneralRoot.driveId}/items/${child.id}/?$expand=children${
                    import.meta.env.VITE_BUILD_TARGET === "kkfiler" ? "" : "($select=name,id)"
                  }`
                );

                actualTemplateFolder = new DriveItem(actualTemplateFolderResponse);
              } else {
                actualTemplateFolder = null;
                break;
              }
            }
          }

          // Load actual template folder

          if (actualTemplateFolder) {
            let institutionTemplates = [];

            if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
              const response = await graphGet(
                `/drives/${templateGeneralRoot.driveId}/items/${actualTemplateFolder.id}/?$expand=children`
              );
              institutionTemplates = response.children.map(i => new DriveItem(i));
            } else {
              const response = await graphGet(
                `/drives/${templateGeneralRoot.driveId}/items/${actualTemplateFolder.id}/children?$expand=thumbnails`
              );
              institutionTemplates = response.value.map(i => new DriveItem(i));
            }

            templateItems.push(...institutionTemplates);
          }
        }
      }
    }
  }
  return templateItems;
}

/**
 * Update items with special items setup
 * @param { Array } templateItems Template items to be updated from setup
 * @returns { object } Object with template groups of template items with setup applied
 */
function handleTemplateItemsBeforePersist(templateItems) {
  const settingsStore = useSettingsStore();
  const templates = FileHelper.addExtensionsToArrayOfFiles(templateItems);
  const documentStore = useDocumentStore();

  if (settingsStore.templateSpecialItems?.length > 0) {
    templates.forEach(t => {
      const specialItem = settingsStore.templateSpecialItems.find(
        si => si.driveId === t.parentReference.driveId && si.itemId === t.id
      );

      if (specialItem) {
        t.folder = true;
        t.specialItemType = specialItem.specialItemType;
        t.suffixIcon = specialItem.suffixIcon;
        t.putInTemplateTypeSubfolder = specialItem.putInTemplateTypeSubfolder;
        t.putInRootFolder = specialItem.putInRootFolder;
        t.documentNameFormat = specialItem.documentNameFormat;

        t.extension = JSON.parse(JSON.stringify(t.extension));
        t.extension.color = specialItem.color;
        t.extension.typeIcon = specialItem.typeIcon;
        t.extension.suffixIcon = specialItem.suffixIcon;

        // Add to specialitemlist in documentStore
        documentStore.addSpecialItem("template", t.parentReference.driveId, t.id, specialItem.specialItemType, false);
      }
    });
  }

  const templateGroups = {};

  templateGroups.folders = templates
    .filter(i => i.folder && !i.specialItemType)
    .sort((a, b) => a.name.localeCompare(b.name, settingsStore.language));

  templateGroups.specialFolders = templates
    .filter(i => i.folder && i.specialItemType)
    .sort((a, b) => a.name.localeCompare(b.name, settingsStore.language));

  templateGroups.files = templates
    .filter(i => !i.folder && !i.specialItemType)
    .sort((a, b) => a.name.localeCompare(b.name, settingsStore.language));

  templateGroups.specialFiles = templates
    .filter(i => !i.folder && i.specialItemType)
    .sort((a, b) => a.name.localeCompare(b.name, settingsStore.language));

  templateGroups.newFolder = [{ newFolder: true, extension: FileHelper.getClosedFolderTemplate() }];
  return templateGroups;
}

/**
 * Clone template folder map
 * @param { object } input Template folder map to be cloned
 * @returns { object } Cloned template folder map
 */
function cloneTemplateForFolderType(input) {
  const clon = {};
  for (const key in input) {
    clon[key] = {};
    for (const key2 in input[key]) {
      clon[key][key2] = input[key][key2];
    }
  }
  return clon;
}
